import { useState, useEffect, useRef } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
        }
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

const TURNSTILE_SITE_KEY =
  (import.meta.env.VITE_TURNSTILE_SITE_KEY as string) || "";

interface Props {
  project: string;
  projectName: string;
}

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "ok"; issueNumber?: number }
  | { kind: "error"; message: string };

export default function InterestForm({ project, projectName }: Props) {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [token, setToken] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    const ensureScript = () =>
      new Promise<void>((resolve) => {
        if (window.turnstile) return resolve();
        const existing = document.querySelector(
          'script[src*="challenges.cloudflare.com/turnstile"]'
        );
        if (existing) {
          existing.addEventListener("load", () => resolve());
          return;
        }
        const s = document.createElement("script");
        s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        document.head.appendChild(s);
      });

    let cancelled = false;
    ensureScript().then(() => {
      if (cancelled || !turnstileRef.current || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "dark",
        callback: (t: string) => setToken(t),
        "error-callback": () => setToken(""),
        "expired-callback": () => setToken(""),
      });
    });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
    };
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ kind: "submitting" });
    try {
      const res = await fetch("/api/contact/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: `Interest: ${projectName}`,
          email,
          message: message || `Interested in ${projectName}.`,
          turnstileToken: token,
          project,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setStatus({
          kind: "error",
          message: data.message || data.error || "Submission failed",
        });
        return;
      }
      setStatus({ kind: "ok", issueNumber: data.issueNumber });
    } catch (err) {
      setStatus({
        kind: "error",
        message: err instanceof Error ? err.message : "Network error",
      });
    }
  };

  if (status.kind === "ok") {
    return (
      <div style={{ textAlign: "center", color: "var(--term-green)", padding: "1rem 0" }}>
        <p style={{ fontSize: "1.125rem", fontWeight: 600, margin: "0 0 0.5rem" }}>
          noted{status.issueNumber ? ` (#${status.issueNumber})` : ""}.
        </p>
        <p style={{ color: "var(--fg-dim)", fontSize: "0.875rem" }}>
          i'll follow up. thanks for the interest.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      style={{ display: "flex", flexDirection: "column", gap: "0.875rem" }}
    >
      <label style={labelStyle}>
        <span style={labelTextStyle}>
          email <span style={{ color: "var(--accent)" }}>*</span>
        </span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="you@example.com"
          style={inputStyle}
        />
      </label>
      <label style={labelStyle}>
        <span style={labelTextStyle}>message</span>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="what caught your eye? (optional)"
          style={{ ...inputStyle, resize: "vertical" }}
        />
      </label>
      {TURNSTILE_SITE_KEY ? (
        <div ref={turnstileRef} />
      ) : (
        <p style={{ fontSize: "0.75rem", color: "var(--fg-dim)", margin: 0 }}>
          (turnstile not configured — dev mode)
        </p>
      )}
      {status.kind === "error" && (
        <p style={{ color: "#ef4444", fontSize: "0.8125rem", margin: 0 }}>
          {status.message}
        </p>
      )}
      <button
        type="submit"
        disabled={
          status.kind === "submitting" ||
          !email ||
          (Boolean(TURNSTILE_SITE_KEY) && !token)
        }
        style={{
          background: "var(--accent)",
          color: "var(--bg)",
          border: "1px solid var(--accent)",
          padding: "0.75rem 1rem",
          cursor: status.kind === "submitting" ? "wait" : "pointer",
          opacity: status.kind === "submitting" ? 0.6 : 1,
          fontWeight: 600,
        }}
      >
        {status.kind === "submitting" ? "sending..." : "send →"}
      </button>
    </form>
  );
}

const labelStyle: React.CSSProperties = { display: "block" };

const labelTextStyle: React.CSSProperties = {
  display: "block",
  fontFamily: "var(--mono)",
  fontSize: "0.75rem",
  color: "var(--fg-dim)",
  marginBottom: "0.35rem",
  letterSpacing: "0.05em",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.1)",
  color: "var(--fg)",
  padding: "0.625rem 0.75rem",
  fontFamily: "var(--mono)",
  fontSize: "0.9375rem",
  outline: "none",
  boxSizing: "border-box",
};
